/**
 * Canonical search-group presentation for the Evidence child timeline: a
 * bordered stack of `InlineSearchBlock`s that animates from streaming
 * "searching…" rows into the collapsed "N sources found" rollup once every
 * search completes. Callers normalize their own row shape down to
 * `EvidenceSearchRow[]`.
 */

import type { ComponentType, ReactNode } from 'react';

import { CollapsedSourcesSummary, InlineSearchBlock } from '@heidi/evidence';
import type { Source } from '@heidi/evidence/contracts';
import { getWorkHostPort } from '../../../work-home/work-home-ports';

export interface EvidenceSearchRow {
  query: string;
  sources: Source[];
  status: 'searching' | 'completed';
}

/** Mirrors `ChatTimeline.getGroupPosition` so per-row affordances match. */
const getGroupPosition = (
  idx: number,
  length: number
): 'first' | 'middle' | 'last' | 'only' => {
  if (length === 1) return 'only';
  if (idx === 0) return 'first';
  if (idx === length - 1) return 'last';
  return 'middle';
};

export interface EvidenceSearchGroupProps {
  rows: EvidenceSearchRow[];
  isStreaming?: boolean;
  /** True while streaming with no answer text yet — keeps the "searching…" animation up. */
  waitingForText?: boolean;
  sessionId?: string | null;
}

export const EvidenceSearchGroup = ({
  rows,
  isStreaming = false,
  waitingForText = false,
  sessionId = null,
}: EvidenceSearchGroupProps): JSX.Element => {
  const SearchGroupTransition = getWorkHostPort('evidence')
    .SearchGroupTransition as ComponentType<{
    allComplete: boolean;
    searchBlocks: ReactNode;
    summary: ReactNode;
  }>;
  const allComplete = !waitingForText && rows.every((row) => row.status === 'completed');
  const allSources = rows.flatMap((row) => row.sources);

  return (
    <SearchGroupTransition
      allComplete={allComplete}
      searchBlocks={
        <div className="flex w-full min-w-0 flex-col overflow-hidden rounded-lg border border-border">
          {rows.map((row, idx) => (
            <InlineSearchBlock
              key={`search-${idx}`}
              query={row.query}
              sources={row.sources}
              status={row.status}
              isStreaming={isStreaming}
              groupPosition={getGroupPosition(idx, rows.length)}
              sessionId={sessionId}
            />
          ))}
        </div>
      }
      summary={<CollapsedSourcesSummary sources={allSources} sessionId={sessionId} />}
    />
  );
};
